import React, { useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate, generatePath } from "react-router-dom";

import I24Check from "assets/icons/I24Check";

import AuthStore from "stores/AuthStore";
import ProfileStore from "stores/ProfileStore";

import Input from "components/Input";
import Checkbox from "components/Checkbox";
import Button from "components/Button";

import { email as envEmail, password as envPassword } from "utils/env";
import { isEmpty, isEmail, composeValidators } from "utils/validation";

import ROUTES from "navigation/routes";

import styleConst from "constants/style";
import { withTranslation, useTranslation } from "react-i18next";

import {
  Background,
  Container,
  LogoContainer,
  Logo,
  Title,
  ItemWrapper,
  Link,
  errorTextStyles,
} from "./styles";

const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>(envEmail);
  const [password, setPassword] = useState<string>(envPassword);
  const [error, setError] = useState<{ email: string; password: string }>({
    email: "",
    password: "",
  });
  const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
  const isEditingEmailRef = useRef(false);

  const onEmailFocus = () => {
    if (isExistingUser) {
      isEditingEmailRef.current = true;
    }
  };

  const onEmailChange = (text: string) => {
    if (isExistingUser && !isEditingEmailRef.current) {
      return;
    }

    setEmail(text);
    setError({ email: "", password: "" });

    if (isExistingUser) {
      setIsExistingUser(false);
      isEditingEmailRef.current = false;
    }
  };

  const onPasswordChange = (text: string) => {
    setPassword(text.trim());
    setError(prev => ({ ...prev, password: "" }));
  };

  const checkEmail = () => {
    const emailError = composeValidators(isEmpty, isEmail)(email);
    setError(prev => ({
      ...prev,
      email: emailError ? t(emailError) : "",
    }));
  };

  const checkForm = () => {
    const emailError = composeValidators(isEmpty, isEmail)(email);
    const passwordError = composeValidators(isEmpty)(password);

    setError({
      email: emailError ? t(emailError) : "",
      password: passwordError ? t(passwordError) : "",
    });
  };

  const onContinue = async () => {
    checkEmail();

    const emailValidation = composeValidators(isEmpty, isEmail)(email);
    if (!emailValidation) {
      try {
        const exists = await AuthStore.validateEmail(email);
        setIsExistingUser(exists);

        if (!exists) {
          await AuthStore.login({ email });
          navigate(generatePath(ROUTES.ONBOARDINGDISABLEDMESSAGE));
          return;
        }

        setPassword(envPassword);
      } catch (error: any) {
        console.log("error :>> ", error);
        if (error?.name === "incorrect_email_or_password") {
          setError(prev => ({
            ...prev,
            email: t("sign-in.email-or-password-error"),
          }));
        } else {
          setError(prev => ({ ...prev, email: error?.message }));
        }
      }
    }
  };

  const onLogin = async () => {
    checkForm();

    const emailErr = composeValidators(isEmpty, isEmail)(email);
    const passErr = composeValidators(isEmpty)(password);

    if (!emailErr && !passErr) {
      try {
        await AuthStore.login({ email, password });
        await ProfileStore.fetch();

        if (
          AuthStore.isNeedOnboarding ||
          AuthStore.isNeedOnboarding === null ||
          AuthStore.isNeedChangePassword
        ) {
          navigate(generatePath(ROUTES.ONBOARDINGDISABLEDMESSAGE));
          return;
        }

        navigate(generatePath(ROUTES.TODAY));
      } catch (error: any) {
        console.log("error :>> ", error);
        if (error?.name === "incorrect_email_or_password") {
          setError(prev => ({
            ...prev,
            password: t("sign-in.email-or-password-error"),
          }));
        } else {
          setError(prev => ({ ...prev, password: error?.message }));
        }
      }
    }
  };

  const onForgotPassword = async () => {
    AuthStore.setEmail(email);
    navigate(generatePath(ROUTES.FORGOT_PASSWORD));
  };

  const toggleIsNeedRemember = (value: boolean) => {
    AuthStore.setIsNeedRememberTokenData(value);
  };

  return (
    <Background>
      <Container>
        <LogoContainer>
          <Logo alt="Logo" />
        </LogoContainer>
        <Title>{t("actions.login")}</Title>
        <ItemWrapper>
          <Input
            value={email}
            onChangeText={onEmailChange}
            onFocus={onEmailFocus}
            placeholder="Email"
            type="email"
            icon={
              isExistingUser ? (
                <I24Check color={styleConst.colors.primary} />
              ) : null
            }
            error={error.email}
            errorTextStyles={errorTextStyles}
          />
        </ItemWrapper>
        {isExistingUser ? (
          <>
            <ItemWrapper>
              <Input
                label={t("sign-in.password")}
                value={password}
                onChangeText={onPasswordChange}
                type="password"
                error={error.password}
                errorTextStyles={errorTextStyles}
                toggleableSecureText
              />
            </ItemWrapper>
            <ItemWrapper>
              <Checkbox
                label={t("sign-in.remember-me")}
                checked={AuthStore.isNeedRememberTokenData}
                toggleChecked={toggleIsNeedRemember}
              />
            </ItemWrapper>
          </>
        ) : null}
        <ItemWrapper>
          <Button
            loading={AuthStore.isValidating || AuthStore.isLoading}
            fontWeight="bold"
            text={isExistingUser ? t("actions.enter") : t("actions.continue")}
            onClick={isExistingUser ? onLogin : onContinue}
          />
        </ItemWrapper>
        {isExistingUser ? (
          <ItemWrapper>
            <Link onClick={onForgotPassword}>
              {t("actions.forgot-password")}
            </Link>
          </ItemWrapper>
        ) : null}
      </Container>
    </Background>
  );
};

export default withTranslation()(observer(SignIn));
